import ParameterValidator from '@mvps-genesis/parameter-validator';
import EventBus from '@mvps-genesis/event-bus';
import {getParent} from '@mvps-genesis/selector-helpers';
import {hasKeyboardModifier} from '@mvps-genesis/event-helpers';
import {isFocusable} from 'tabbable';
import {MenuItem} from './index';

export default class Menu {

	static selector = '[role="menu"]';

	static fromNodeList (nodeList, options) {
		return Array.from(nodeList).map(node => new Menu({
			element: node,
			...options
		}));
	};

	static events = {
		close: 'Menu.close',
		enterSubMenu: 'Menu.enterSubMenu'
	};

	_validator = null;

	childMenuItems = null;

	constructor (options) {

		this._validator = new ParameterValidator(this);
		this.eventBus = new EventBus();

		this.init(options);

	}

	init (options) {
		this.validateOptions(options);
		this.setChildMenuItems();
	}

	validateOptions (options) {

		this._options = options;
		this.breakpoint = this._validator.isString('options.breakpoint', options.breakpoint || '60em', {required: false});
		this.element = this._validator.isElement('options.element', options.element);
		this.navigateOnDown = this._validator.isBoolean('options.navigateOnDown', options.navigateOnDown === void 0 || options.navigateOnDown === null ? true : options.navigateOnDown);
		this.navigateOnEnter = this._validator.isBoolean('options.navigateOnEnter', options.navigateOnEnter === void 0 || options.navigateOnEnter === null ? true : options.navigateOnEnter);
		this.navigateOnSpace = this._validator.isBoolean('options.navigateOnSpace', options.navigateOnSpace === void 0 || options.navigateOnSpace === null ? true : options.navigateOnSpace);
		this.parentMenu = options.parentMenu;
		this.parentMenuBar = options.parentMenuBar;
		this.parentMenuBarItem = options.parentMenuBarItem;
		this.parentMenuItem = options.parentMenuItem;
		this.root = this.isRoot();
		this.subMenuFocusDelay = this._validator.isInt('options.subMenuFocusDelay', options.subMenuFocusDelay === void 0 || options.subMenuFocusDelay === null ? 100 : options.subMenuFocusDelay);

	}

	setChildMenuItems () {
		const menuItems = this._validator.isElementList('menuItems', MenuItem.selector, {container: this.element});
		const childMenuItems = Array.from(menuItems).filter(menuItem => {
			return getParent(menuItem, Menu.selector) === this.element;
		});

		if (!childMenuItems || childMenuItems.length === 0) {
			// eslint-disable-next-line no-console
			console.error(`Menu :: unable to find any menuitem children`);
			return false;
		}

		this.childMenuItems = MenuItem.fromNodeList(childMenuItems, {
			parentMenu: this,
			parentMenuBar: this.parentMenuBar,
			parentMenuBarItem: this.parentMenuBarItem,
			parentOptions: {
				breakpoint: this.breakpoint,
				navigateOnDown: this.navigateOnDown,
				navigateOnEnter: this.navigateOnEnter,
				navigateOnSpace: this.navigateOnSpace,
				subMenuFocusDelay: this.subMenuFocusDelay
			}
		});

		return true;
	}

	get menuItems () {
		return this.childMenuItems && this.childMenuItems.filter(menuItem => isFocusable(menuItem.element));
	}

	addEventListener (eventName, callback, recursive = false) {
		this._validator.inSet('eventName', eventName, Object.values(Menu.events));
		this._validator.isFunction('callback', callback);

		this.eventBus.addEventListener(eventName, callback);

		if (recursive) {
			for (let menuItem of this.menuItems) {
				if (menuItem.subMenu) {
					menuItem.subMenu.addEventListener(eventName, callback, recursive);
				}
			}
		}
	}

	removeEventListener (eventName, callback, recursive = false) {
		this._validator.inSet('eventName', eventName, Object.values(Menu.events));
		this._validator.isFunction('callback', callback);

		this.eventBus.removeEventListener(eventName, callback);

		if (recursive) {
			for (let menuItem of this.menuItems) {
				if (menuItem.subMenu) {
					menuItem.subMenu.removeEventListener(eventName, callback, recursive);
				}
			}
		}
	}

	isRoot () {
		return !this.parentMenu && !this.parentMenuBar && !this.parentMenuBarItem && !this.parentMenuItem;
	}

	isMobile () {
		return this.breakpoint ? window.matchMedia(`(max-width: ${this.breakpoint})`).matches : false;
	}

	resetMenuItemsState () {
		for (let menuItem of this.menuItems) {
			this.resetMenuItemState(menuItem);
		}
	}

	resetMenuItemState (menuItem) {
		menuItem.setTabIndex(-1);
		menuItem.setAriaExpanded(false);
	}

	focusLastMenuItem () {
		this.resetMenuItemsState();

		const last = this.menuItems[this.menuItems.length - 1];
		this.focusMenuItem(last);
	}

	focusFirstMenuItem () {
		this.resetMenuItemsState();

		const first = this.menuItems[0];
		this.focusMenuItem(first);
	}

	focusNextMenuItem (currentMenuItem) {
		this.resetMenuItemState(currentMenuItem);

		const currentIndex = this.menuItems.findIndex(menuItem => menuItem === currentMenuItem);
		const nextMenuItem = this.menuItems[currentIndex + 1];
		if (nextMenuItem) {
			this.focusMenuItem(nextMenuItem);
		}
		else if (this.parentMenu) {
			this.parentMenu.focusNextMenuItem(this.parentMenuItem);
		}
		else {
			this.focusFirstMenuItem();
		}
	}

	focusPreviousMenuItem (currentMenuItem) {
		this.resetMenuItemState(currentMenuItem);

		const currentIndex = this.menuItems.findIndex(menuItem => menuItem === currentMenuItem);
		const previousMenuItem = this.menuItems[currentIndex - 1];
		if (previousMenuItem) {
			this.focusMenuItem(previousMenuItem);
		}
		else if (this.parentMenuItem && this.parentMenuItem.parentMenu) {
			this.focusMenuItem(this.parentMenuItem);
		}
		else {
			this.focusLastMenuItem();
		}
	}

	focusMenuItem (menuItem) {
		menuItem.setTabIndex(0);
		menuItem.setFocus();
	}

	closeMenu () {
		if (this.parentMenuItem) {
			this.resetMenuItemsState();
			this.parentMenuItem.setAriaExpanded(false);
			this.focusMenuItem(this.parentMenuItem);
		}
		this.eventBus.dispatchEvent(Menu.events.close, {
			menu: this
		});
	}

	searchByFirstCharacter (key, target) {
		const index = this.menuItems.findIndex(menuItem => menuItem === target);
		if (index !== -1) {
			const right = this.menuItems.slice(index + 1);
			const left = this.menuItems.slice(0, index);
			const match = right.concat(left).find(menuItem => menuItem.getFirstCharacter() === key);
			if (match) {
				this.resetMenuItemsState();
				this.focusMenuItem(match);
			}
		}
	}

	onOther (e, target) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		this.searchByFirstCharacter(e.key, target);

		return true;
	}

	onDown (e, target) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		if (target.subMenu && !this.isMobile() && this.navigateOnDown) {
			target.dispatchEvent(Menu.events.enterSubMenu);
			setTimeout(() => {
				target.setTabIndex(-1);
				target.setAriaExpanded(true);
				target.subMenu.focusFirstMenuItem();
			}, this.subMenuFocusDelay);
		}
		else {
			this.focusNextMenuItem(target);
		}

		return true;
	}

	onEsc (e) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		this.closeMenu();

		return true;
	}

	onEnd (e) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		this.focusLastMenuItem();

		return true;
	}

	onEnter (e, target) {
		if (target.subMenu && this.navigateOnEnter) {
			if (hasKeyboardModifier(e)) {
				return false;
			}
			e.preventDefault();

			target.dispatchEvent(Menu.events.enterSubMenu);
			setTimeout(() => {
				target.setTabIndex(-1);
				target.setAriaExpanded(true);
				target.subMenu.focusFirstMenuItem();
			}, this.subMenuFocusDelay);
		}

		return true;
	}

	onHome (e, target) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		this.focusFirstMenuItem(target);
		return true;
	}

	onLeft (e, target) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		if (this.isMobile()) {
			this.focusPreviousMenuItem(target);
			return true;
		}
		else if (this.parentMenuBar) {
			this.resetMenuItemsState();
			if (this.parentMenu) {
				this.parentMenu.resetMenuItemsState();
			}
		}

		return false;
	}

	onRight (e, target) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		if (this.isMobile()) {
			this.focusNextMenuItem(target);
			return true;
		}
		else if (this.parentMenuBar) {
			this.resetMenuItemsState();
			if (this.parentMenu) {
				this.parentMenu.resetMenuItemsState();
			}
		}

		return false;
	}

	onTab (e, target) {
		if (!this.isMobile() || hasKeyboardModifier(e, {shift: false})) {
			return false;
		}
		e.preventDefault();

		// eslint-disable-next-line object-property-newline
		if (hasKeyboardModifier(e, {alt: false, ctrl: false, meta: false})) {
			this.focusPreviousMenuItem(target);
		}
		else {
			this.focusNextMenuItem(target);
		}

		return true;
	}

	onSpace (e, target) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		if (target.subMenu && this.navigateOnSpace) {
			target.dispatchEvent(Menu.events.enterSubMenu);
			setTimeout(() => {
				target.setTabIndex(-1);
				target.setAriaExpanded(true);
				target.subMenu.focusFirstMenuItem();
			}, this.subMenuFocusDelay);
		}
		else {
			target.dispatchEvent('click');
		}

		return true;
	}

	onUp (e, target) {
		if (hasKeyboardModifier(e)) {
			return false;
		}
		e.preventDefault();

		this.focusPreviousMenuItem(target);

		return true;
	}

	// eslint-disable-next-line max-lines-per-function
	handleEvent (e, target) {
		switch (e.key) {

		case 'Down':
		case 'ArrowDown':
			return this.onDown(e, target);

		case 'Left':
		case 'ArrowLeft':
			return this.onLeft(e, target);

		case 'Right':
		case 'ArrowRight':
			return this.onRight(e, target);

		case 'Up':
		case 'ArrowUp':
			return this.onUp(e, target);

		case 'End':
			return this.onEnd(e, target);

		case 'Enter':
			return this.onEnter(e, target);

		case 'Esc':
		case 'Escape':
			return this.onEsc(e, target);

		case 'Home':
			return this.onHome(e, target);

		case ' ':
		case 'Space':
		case 'Spacebar':
			return this.onSpace(e, target);

		case 'Tab':
			return this.onTab(e, target);

		default:
			return this.onOther(e, target);

		}
	}

	destroyEvents () {
		for (let menuItem of this.menuItems) {
			menuItem.destroyEvents();
		}
	}

};
